import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';
import { FiGithub, FiLinkedin, FiTwitter, FiInstagram } from 'react-icons/fi';
import { Link } from 'react-scroll';

const Footer = () => {
    const containerRef = useRef(null);
    const wordmarkRef = useRef(null);

    const socialLinks = [
        { icon: <FiGithub size={20} />, url: 'https://github.com/ahmdhzq', label: 'GitHub' },
        { icon: <FiLinkedin size={20} />, url: 'https://www.linkedin.com/in/ahmdhzq18/', label: 'LinkedIn' },
        { icon: <FiTwitter size={20} />, url: 'https://twitter.com/Ahmadha24700995', label: 'Twitter' },
        { icon: <FiInstagram size={20} />, url: 'https://www.instagram.com/ahmd_haziqqq/', label: 'Instagram' },
    ];

    const navLinks = ["Home", "About", "Projects", "Contact"];

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.fromTo('.footer-item',
                { y: 20, opacity: 0 },
                {
                    y: 0, opacity: 1, stagger: 0.08, duration: 0.6, ease: "power3.out",
                    scrollTrigger: { trigger: containerRef.current, start: 'top 90%' }
                }
            );

            // Wordmark split animation - wait for fonts
            document.fonts.ready.then(() => {
                if (wordmarkRef.current) {
                    const split = new SplitText(wordmarkRef.current, { type: "chars", charsClass: "wordmark-char" });
                    gsap.fromTo(split.chars,
                        { y: 60, opacity: 0 },
                        {
                            y: 0, opacity: 1, stagger: 0.04, duration: 0.8, ease: "back.out(1.5)",
                            scrollTrigger: { trigger: wordmarkRef.current, start: 'top 95%' }
                        }
                    );
                }
            });
        }, containerRef);

        return () => ctx.revert();
    }, []);

    return (
        <footer ref={containerRef} className="bg-[#111] font-jakarta border-t-4 border-[#FFE600]">
            {/* Top section */}
            <div className="max-w-7xl mx-auto px-6 pt-16 pb-10">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8 mb-12">
                    {/* Logo + tagline */}
                    <div className="footer-item">
                        <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: '1.5rem', color: 'white', marginBottom: 8 }}>Ahmdhzq</div>
                        <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 'var(--text-xs)', color: '#555' }}>Engineering high-impact digital solutions.</p>
                    </div>

                    {/* Nav links */}
                    <nav className="footer-item flex flex-wrap gap-6">
                        {navLinks.map(link => (
                            <Link
                                key={link}
                                to={link.toLowerCase()}
                                smooth={true}
                                duration={500}
                                style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.18em', color: '#9ca3af', cursor: 'pointer' }}
                                className="hover:text-[#FFE600] transition-colors"
                            >
                                {link}
                            </Link>
                        ))}
                    </nav>

                    {/* Social Icons */}
                    <div className="footer-item flex gap-3">
                        {socialLinks.map((link, i) => (
                            <a
                                key={i}
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={link.label}
                                className="w-10 h-10 flex items-center justify-center bg-transparent border-2 border-[#333] rounded-lg text-gray-400 hover:border-[#FFE600] hover:text-[#FFE600] transition-colors"
                            >
                                {link.icon}
                            </a>
                        ))}
                    </div>
                </div>

                {/* Divider */}
                <div className="border-t border-[#222] mb-6"></div>

                <div className="footer-item flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 'var(--text-xs)', color: '#444' }}>
                        &copy; {new Date().getFullYear()} Ahmad Haziq. All Rights Reserved.
                    </p>
                    <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 'var(--text-xs)', color: '#333' }}>
                        Built with React + GSAP
                    </p>
                </div>
            </div>

            {/* Wordmark */}
            <div className="overflow-hidden border-t border-[#222]">
                <h1
                    ref={wordmarkRef}
                    className="font-black text-center leading-none select-none py-4 px-6 text-outline-yellow"
                    style={{
                        fontSize: '14vw',
                        color: 'transparent',
                        WebkitTextStroke: '1.5px #333',
                        lineHeight: 0.85,
                    }}
                >
                    AHMDHZQ
                </h1>
            </div>
        </footer>
    );
};

export default Footer;